import { useRef, useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/utils/cn";

/**
 * "i" explanation next to a field. Opens on hover, on keyboard focus and on tap
 * (hover alone does not exist on touch screens), closes on leave, blur or Escape.
 */
export function InfoTip({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number>();
  const pointer = useRef<string>("");
  const show = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        aria-label={label}
        onPointerDown={(e) => (pointer.current = e.pointerType)}
        onMouseEnter={() => pointer.current !== "touch" && show()}
        onMouseLeave={hide}
        // Keyboard focus opens it; a tap is handled by onClick so it does not open then close.
        onFocus={(e) => e.currentTarget.matches(":focus-visible") && show()}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          window.clearTimeout(closeTimer.current);
          setOpen((o) => (pointer.current === "touch" ? !o : true));
        }}
        className={cn(
          "relative inline-grid size-5 shrink-0 cursor-help place-items-center rounded-full after:absolute after:-inset-2.5 after:content-[''] text-muted-foreground transition-colors",
          "hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        <Info className="size-3.5" aria-hidden />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="w-72 text-xs leading-relaxed"
        onMouseEnter={show}
        onMouseLeave={hide}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
